import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { DESTINATION_REGIONS } from '../../utils/destinations';
import { useModal } from '../../context/ModalContext';

export default function CountrySelector({ 
  selectedCountries = [], 
  onChange, 
  multiSelect = true,
  maxSelect = 5
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRegions, setExpandedRegions] = useState({ '熱門': true });
  const { showAlert } = useModal();

  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  const handleSelect = (country) => {
    if (!multiSelect) {
      onChange([country]);
      return;
    }

    const isSelected = selectedCountries.includes(country);
    if (isSelected) {
      onChange(selectedCountries.filter(c => c !== country));
    } else {
      if (selectedCountries.length >= maxSelect) {
        showAlert('提示', `最多只能選擇 ${maxSelect} 個國家`);
        return;
      }
      onChange([...selectedCountries, country]);
    }
  };

  // 根據搜尋過濾
  const filteredRegions = useMemo(() => {
    if (!searchQuery.trim()) return DESTINATION_REGIONS;

    const lowerQuery = searchQuery.toLowerCase();
    const result = {};
    
    Object.entries(DESTINATION_REGIONS).forEach(([region, countries]) => {
      const matchedCountries = countries.filter(c => c.toLowerCase().includes(lowerQuery));
      if (matchedCountries.length > 0) {
        result[region] = matchedCountries;
      }
    });
    
    return result;
  }, [searchQuery]);

  // 如果有搜尋字串，自動展開所有有結果的區域
  const displayExpanded = useMemo(() => {
    if (searchQuery.trim()) {
      const allExpanded = {};
      Object.keys(filteredRegions).forEach(r => { allExpanded[r] = true; });
      return allExpanded;
    }
    return expandedRegions;
  }, [searchQuery, filteredRegions, expandedRegions]);

  return (
    <div style={{ width: '100%', background: 'transparent' }}>
      
      {/* 搜尋框 */}
      <div style={{ padding: '0 0 16px 0', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ 
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center',
          background: '#f8fafc',
          borderRadius: '16px',
          border: '1px solid #f1f5f9',
          padding: '10px 16px'
        }}>
          <Search size={18} color="#9ca3af" />
          <input 
            type="text" 
            placeholder="搜尋國家..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              marginLeft: '10px',
              fontSize: '15px',
              color: '#1a1a1a'
            }}
          />
        </div>
        {multiSelect && selectedCountries.length > 0 && (
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280' }}>
            已選擇 ({selectedCountries.length}/{maxSelect}):
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {selectedCountries.map(c => (
                <span key={c} style={{
                  background: '#eef4f9', color: '#4e8bca', padding: '6px 12px', 
                  borderRadius: '20px', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  {c}
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleSelect(c); }}
                    style={{ background: 'none', border: 'none', color: '#4e8bca', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 區域列表 */}
      <div>
        {Object.keys(filteredRegions).length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            找不到符合的國家
          </div>
        ) : (
          Object.entries(filteredRegions).map(([region, countries]) => {
            const isExpanded = displayExpanded[region];
            
            return (
              <div key={region} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => toggleRegion(region)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 4px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#374151',
                    textAlign: 'left'
                  }}
                >
                  {region}
                  {isExpanded ? <ChevronUp size={18} color="#9ca3af" /> : <ChevronDown size={18} color="#9ca3af" />}
                </button>
                
                {isExpanded && (
                  <div style={{ padding: '0 4px 16px 4px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {countries.map(country => {
                      const isSelected = selectedCountries.includes(country);
                      return (
                        <button
                          key={country}
                          type="button"
                          onClick={() => handleSelect(country)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: '1px solid',
                            borderColor: isSelected ? '#4e8bca' : '#e5e7eb',
                            background: isSelected ? '#eef4f9' : 'white',
                            color: isSelected ? '#4e8bca' : '#6b7280',
                            fontSize: '14px',
                            fontWeight: isSelected ? '600' : '400',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: isSelected ? '0 2px 8px rgba(78, 139, 202, 0.15)' : 'none'
                          }}
                        >
                          {isSelected && <Check size={14} strokeWidth={3} />}
                          {country}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
